import React, { useState, Children, Fragment } from 'react';
import { ChevronDownIcon } from 'lucide-react';
interface CategoryItem {
  title: string;
  url: string;
  active?: boolean | number;
  children?: CategoryItem[];
}
const categories: CategoryItem[] = [{
  title: 'Product Lines Available at Peak',
  url: '',
  children: [{
    title: 'Fast Wire EDM – Moly Wire',
    url: 'EDMNetworkFastWire.html'
  }, {
    title: 'Seibu America',
    url: 'SeibuWireEDM.html'
  }, {
    title: 'EDM Network',
    url: 'EDMNetwork.html'
  }, {
    title: 'Spinner Milling & Turning',
    url: 'SpinnerMachineTools.html'
  }, {
    title: 'Breton Machine Tools',
    url: 'BretonMachineTools.html'
  }, {
    title: 'Bodor Laser Cutting',
    url: 'BodorLaserCutting.html'
  }]
}, {
  title: 'CNC Milling',
  url: '59m7/cnc-milling.html',
  children: [{
    title: '3 & 4-Axis',
    url: '59m7/cnc-milling/3-4-axis.html'
  }, {
    title: 'CNC Bridge Mill',
    url: '59m7/cnc-milling/cnc-bridge-mill.html'
  }, {
    title: '5-Axis',
    url: '59m7/cnc-milling/5-axis.html'
  }, {
    title: '5-Axis Gantry',
    url: '59m7/cnc-milling/5-axis-gantry.html'
  }]
}, {
  title: 'CNC Turning',
  url: '59m7/cnc-turning.html',
  children: [{
    title: 'Ultra-Precision Turning',
    url: '59m7/cnc-turning/ultra-precision-turning.html'
  }, {
    title: 'Universal Turning',
    url: '59m7/cnc-turning/universal-turning.html'
  }, {
    title: 'Multi-Turret',
    url: '59m7/cnc-turning/multi-turret.html'
  }]
}, {
  title: 'EDM',
  url: '59m7/edm.html',
  children: [{
    title: 'Wire EDM',
    url: '59m7/edm/wire-edm.html'
  }, {
    title: 'Sinker EDM',
    url: '59m7/edm/sinker-edm.html'
  }, {
    title: 'Driller EDM',
    url: '59m7/edm/driller-edm.html'
  }]
}, {
  title: 'Laser',
  url: '59m7/laser.html',
  children: [{
    title: 'Laser Texturing',
    url: '59m7/laser/laser-texturing.html'
  }, {
    title: 'Fiber Laser Cutting',
    url: '59m7/laser/fiber-laser-cutting.html'
  }, {
    title: 'Laser Micromachining',
    url: '59m7/laser/laser-micromachining.html'
  }]
}, {
  title: 'Automation',
  url: '59m7/automation.html'
}, {
  title: 'Used Machines',
  url: '59m7/used-machines.html',
  active: 1,
  children: [{
    title: 'Used CNC Machining Centers',
    url: '59m7/used-machines/used-cnc-machining-centers.html'
  }, {
    title: 'Used EDM Machines',
    url: '59m7/used-machines/used-edm-machines.html',
    active: 1,
    children: [{
      title: 'Used Wire EDM',
      url: '59m7/used-machines/used-edm-machines/used-wire-edm.html'
    }, {
      title: 'Used Sinker EDM',
      url: '59m7/used-machines/used-edm-machines/used-sinker-edm.html'
    }, {
      title: 'Used Hole Drilling EDM',
      url: '59m7/used-machines/used-edm-machines/used-hole-drilling-edm.html'
    }]
  }, {
    title: 'Used Chillers & Transformers',
    url: '59m7/used-machines/used-chillers-transformers.html'
  }]
}];
// Walk the entire tree and collect titles that should be expanded on initial
// render. For EVERY item marked active, we include the full path from root to
// that item (the item itself + all its ancestors), so multiple active items at
// different depths each expand their own sub-tree.
function collectActiveExpansions(items: CategoryItem[], ancestors: string[] = []): string[] {
  const result: string[] = [];
  for (const item of items) {
    const path = [...ancestors, item.title];
    if (item.active) result.push(...path);
    if (item.children) {
      result.push(...collectActiveExpansions(item.children, path));
    }
  }
  return result;
}
const indentByDepth = ['pl-5', 'pl-7', 'pl-10', 'pl-14'];
interface ChevronToggleProps {
  isExpanded: boolean;
  onClick: () => void;
  variant: 'navy' | 'gray';
}
function ChevronToggle({
  isExpanded,
  onClick,
  variant
}: ChevronToggleProps) {
  const hoverBg = variant === 'navy' ? 'hover:bg-white/15' : 'hover:bg-gray-200';
  return <button type="button" onClick={onClick} aria-label={isExpanded ? 'Collapse section' : 'Expand section'} className={`flex-shrink-0 p-1 rounded ${hoverBg} transition-colors`} data-id="element-4031">
      <ChevronDownIcon className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} data-id="element-4032" />
    </button>;
}
interface NestedItemsProps {
  items: CategoryItem[];
  depth: number;
  expanded: Set<string>;
  toggle: (title: string) => void;
}
function NestedItems({
  items,
  depth,
  expanded,
  toggle
}: NestedItemsProps) {
  const indent = indentByDepth[Math.min(depth, indentByDepth.length - 1)];
  return <div className={depth === 1 ? 'bg-gray-50 border-t border-gray-200' : ''} data-id="element-4033">
      {items.map((item, idx) => {
      const isLast = idx === items.length - 1;
      const hasChildren = !!item.children && item.children.length > 0;
      const isExpanded = hasChildren && expanded.has(item.title);
      const rowBorder = !isLast || isExpanded ? 'border-b border-gray-200' : '';
      const isActive = !!item.active;
      const textColor = isActive ? 'text-orange font-bold' : 'text-gray-700 font-medium';
      const baseRow = `flex items-center justify-between ${indent} pr-2 py-2.5 text-sm ${textColor} ${rowBorder}`;
      let row: React.ReactNode;
      if (item.url && hasChildren) {
        // Link + expandable: anchor for the label, separate chevron button.
        row = <div className={`${baseRow} hover:bg-gray-100 transition-colors`} data-id="element-4034">
              <a href={item.url} className="flex-1 hover:text-orange transition-colors" data-id="element-4035">
                {item.title}
              </a>
              <ChevronToggle isExpanded={isExpanded} onClick={() => toggle(item.title)} variant="gray" data-id="element-4036" />
            </div>;
      } else if (item.url) {
        // Plain link, no children.
        row = <a href={item.url} className={`${baseRow} hover:bg-gray-100 hover:text-orange transition-colors`} data-id="element-4037">
              <span className="flex-1" data-id="element-4038">{item.title}</span>
            </a>;
      } else if (hasChildren) {
        // Empty URL + has children → whole row toggles.
        row = <button type="button" onClick={() => toggle(item.title)} className={`w-full text-left ${baseRow} hover:bg-gray-100 hover:text-orange transition-colors`} data-id="element-4039">
              <span className="flex-1" data-id="element-4040">{item.title}</span>
              <ChevronDownIcon className={`w-4 h-4 flex-shrink-0 ml-2 transition-transform ${isExpanded ? 'rotate-180' : ''}`} data-id="element-4041" />
            </button>;
      } else {
        // Empty URL, no children → static label.
        row = <span className={baseRow} data-id="element-4042">
              <span className="flex-1" data-id="element-4043">{item.title}</span>
            </span>;
      }
      return <Fragment key={item.title} data-id="element-4044">
            {row}
            {isExpanded && <NestedItems items={item.children!} depth={depth + 1} expanded={expanded} toggle={toggle} data-id="element-4045" />}
          </Fragment>;
    })}
    </div>;
}
export function CategorySidebar() {
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(collectActiveExpansions(categories)));
  const toggle = (title: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(title)) next.delete(title);else next.add(title);
      return next;
    });
  };
  return <div className="w-full lg:w-64 flex-shrink-0" data-id="element-4046">
      <nav className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden lg:sticky lg:top-8" data-id="element-4047">
        {categories.map(item => {
        const hasChildren = !!item.children && item.children.length > 0;
        const isExpanded = hasChildren && expanded.has(item.title);
        const isActive = !!item.active;
        const activeBg = isActive ? 'bg-white/15 font-bold' : 'font-medium';
        const baseRow = `flex items-center justify-between px-5 py-3 text-sm border-b border-white/10 ${activeBg}`;
        let row: React.ReactNode;
        if (item.url && hasChildren) {
          row = <div className={`${baseRow} hover:bg-white/10 transition-colors`} data-id="element-4048">
                <a href={item.url} className="flex-1 hover:text-orange transition-colors" data-id="element-4049">
                  {item.title}
                </a>
                <ChevronToggle isExpanded={isExpanded} onClick={() => toggle(item.title)} variant="navy" data-id="element-4050" />
              </div>;
        } else if (item.url) {
          row = <a href={item.url} className={`${baseRow} hover:bg-white/10 hover:text-orange transition-colors`} data-id="element-4051">
                <span className="flex-1" data-id="element-4052">{item.title}</span>
              </a>;
        } else if (hasChildren) {
          row = <button type="button" onClick={() => toggle(item.title)} className={`w-full text-left ${baseRow} hover:bg-white/10 transition-colors`} data-id="element-4053">
                <span className="flex-1" data-id="element-4054">{item.title}</span>
                <ChevronDownIcon className={`w-4 h-4 flex-shrink-0 ml-2 transition-transform ${isExpanded ? 'rotate-180' : ''}`} data-id="element-4055" />
              </button>;
        } else {
          row = <span className={baseRow} data-id="element-4056">
                <span className="flex-1" data-id="element-4057">{item.title}</span>
              </span>;
        }
        return <Fragment key={item.title} data-id="element-4058">
              <div className="bg-[#002d4d] text-white" data-id="element-4059">{row}</div>
              {isExpanded && <NestedItems items={item.children!} depth={1} expanded={expanded} toggle={toggle} data-id="element-4060" />}
            </Fragment>;
      })}
      </nav>
    </div>;
}